// Ported from daisyUI dock (v5, MIT). See LICENSE-THIRD-PARTY.md.

/**
 * @component ry-dock
 * @category  nav
 * @summary   Fixed bottom navigation bar (daisyUI `.dock` equivalent — mobile-style "dock"/tab-bar). A horizontal row of icon+label items pinned to the bottom of the viewport. Exactly one item is active at a time; clicking an item makes it the active one and fires `ry:change`. daisyUI ships dock as CSS-only — ry-dock adds the JS selection model, keyboard navigation, and ARIA. Items are authored as `<ry-dock-item icon label>` children and decorated in place at setup.
 *
 * Reference: daisyUI v5 `dock.css`. The visual shape (fixed bottom bar, icon
 * stacked over a small label, active item gets a pill underline) is preserved;
 * ry-ui re-implements it with tokens (no Tailwind utility classes) and layers
 * FUNCTION (this file) / FORM (ry-dock.structure.css) / THEME (ry-dock.theme.css).
 *
 * @attribute size   'xs' | 'sm' | 'md' | 'lg' | 'xl'   default="md"   Bar height + label scale. Pure CSS hook on the host.
 * @attribute aria-label   string?   default="Bottom navigation"   Accessible name for the nav landmark. Override when multiple navs exist.
 *
 * @property  value          string    Value of the active item (its `value` attribute, falling back to its `label`). Setting it activates the matching item and fires `ry:change`. No-op if no item matches.
 * @property  activeIndex    number    Index of the active item. Returns -1 if none active (transient — setup() guarantees one is active when items exist). Setting it activates that index and fires `ry:change`.
 *
 * @method    select(index)   Activate the item at `index`. No-op (no event) if the index is out of range or the item is disabled. Fires `ry:change` on success.
 *
 * @event ry:beforechange
 *        detail: { index: number, oldIndex: number, value: string, item: HTMLElement, previous: string }
 *        fires:  inside `select()` — click, Enter/Space, `select(i)`, the `value` / `activeIndex` setters — after the disabled / already-active no-op checks, before the active item moves. `value` / `index` / `item` describe the item ABOUT to activate; `oldIndex` / `previous` the current index / value.
 *        cancelable: preventDefault() keeps the current item active; `ry:change` does not fire.
 *
 * @event ry:change
 *        detail: { index: number, oldIndex: number, value: string, item: HTMLElement }
 *        fires:  after the active item changes — via click, keyboard activation, the `select()` method, or the `value`/`activeIndex` setters. `index` is the NEW active index, `oldIndex` the previous one. Does NOT fire on initial mount, nor when re-selecting the already-active item.
 *
 * @keyboard  ArrowRight / ArrowLeft — move focus to the next / previous enabled item (wraps; skips disabled). ArrowDown / ArrowUp — same as Right / Left (vertical-key affordance). Home / End — focus first / last enabled item. Enter / Space — activate the focused item. Arrow keys move focus only; activation requires Enter/Space (manual-activation pattern, unlike ry-tabs).
 *
 * @state     Per-item: active item has `[active]`, `data-ry-state="active"`, and `aria-current="page"`. Inactive items have `data-ry-state=""`. Disabled items have `[disabled]` + `aria-disabled="true"`.
 *
 * @content   `<ry-dock-item>` children. Each is decorated in place (NOT replaced) into a focusable item: an `<a>` if it has `href`, otherwise a `role="button"`. Authored attributes: `icon`, `label`, `value`, `active`, `disabled`, `href`.
 *
 * @dom
 *   ry-dock[size?]   data-ry-target="dock"   role="navigation"   aria-label="…"
 *     > ry-dock-item   data-ry-target="item"   class="ry-dock__item"
 *         [active]? data-ry-state="active|"  aria-current="page"?  tabindex="0|-1"
 *         role="button" (or rendered as <a href> link)   [disabled]? aria-disabled?
 *       > ry-icon.ry-dock__icon            (when [icon] set)
 *       > span.ry-dock__label              (when [label] set)   data-ry-target="label"
 *
 * @gotcha  `<ry-dock-item>` children are DECORATED in place, not replaced (unlike `<ry-accordion-item>`). Query items with `[data-ry-target="item"]` OR `ry-dock-item`. The item is the focusable/clickable element itself — there is no inner wrapper button.
 *
 * @gotcha  Arrow keys move focus only; they do NOT activate (manual-activation pattern). This is the OPPOSITE of `ry-tabs`, where arrows follow-focus and auto-activate. To activate a dock item from the keyboard you must press Enter or Space. This matches the "tab bar" mental model where moving focus shouldn't navigate away.
 *
 * @gotcha  Disabled items are skipped by keyboard navigation and ignore clicks / `select()` — no `ry:change` fires. `select(indexOfDisabled)` is a silent no-op. They remain in the DOM and keep their index, so indices are stable.
 *
 * @gotcha  `value` falls back to `label` when no `value` attribute is authored. If two items share the same effective value, `el.value = x` activates the FIRST match. Give items explicit unique `value` attributes when labels collide.
 *
 * @gotcha  Setup-once: items are read once in setup(). Adding `<ry-dock-item>` children after mount does not wire them. Mark `[active]` on at most one item at author time; if none is marked, the first enabled item starts active. Adding `[active]` post-mount does NOT shift selection — use `el.select(i)` / `el.value =` / `el.activeIndex =`.
 *
 * @example basic
 * ```html
 * <ry-dock>
 *   <ry-dock-item icon="home" label="Home" active></ry-dock-item>
 *   <ry-dock-item icon="search" label="Search"></ry-dock-item>
 *   <ry-dock-item icon="settings" label="Settings"></ry-dock-item>
 * </ry-dock>
 * ```
 *
 * @example with-active
 * ```html
 * <ry-dock>
 *   <ry-dock-item icon="home" label="Home"></ry-dock-item>
 *   <ry-dock-item icon="heart" label="Saved" active></ry-dock-item>
 *   <ry-dock-item icon="user" label="Profile"></ry-dock-item>
 * </ry-dock>
 * ```
 *
 * @example sizes
 * ```html
 * <ry-dock size="xs">
 *   <ry-dock-item icon="home" label="Home" active></ry-dock-item>
 *   <ry-dock-item icon="search" label="Search"></ry-dock-item>
 * </ry-dock>
 * <ry-dock size="xl">
 *   <ry-dock-item icon="home" label="Home" active></ry-dock-item>
 *   <ry-dock-item icon="search" label="Search"></ry-dock-item>
 * </ry-dock>
 * ```
 *
 * @example with-links
 * ```html
 * <ry-dock>
 *   <ry-dock-item icon="home" label="Home" href="/" active></ry-dock-item>
 *   <ry-dock-item icon="info" label="About" href="/about"></ry-dock-item>
 *   <ry-dock-item icon="settings" label="Settings" disabled></ry-dock-item>
 * </ry-dock>
 * ```
 *
 * @tutorial renders-n-items
 * ```js
 * const el = mount(`
 *   <ry-dock>
 *     <ry-dock-item icon="home" label="Home"></ry-dock-item>
 *     <ry-dock-item icon="search" label="Search"></ry-dock-item>
 *     <ry-dock-item icon="settings" label="Settings"></ry-dock-item>
 *   </ry-dock>
 * `);
 * const items = el.querySelectorAll('[data-ry-target="item"]');
 * await expectEqual(items.length, 3, 'three items decorated');
 * await expectEqual(el.activeIndex, 0, 'first item active by default');
 * ```
 *
 * @tutorial click-activates-and-emits
 * ```js
 * const el = mount(`
 *   <ry-dock>
 *     <ry-dock-item icon="home" label="Home"></ry-dock-item>
 *     <ry-dock-item icon="search" label="Search"></ry-dock-item>
 *   </ry-dock>
 * `);
 * const items = el.querySelectorAll('[data-ry-target="item"]');
 * await expectEvent(el, 'ry:change', () => items[1].click());
 * await expectEqual(el.activeIndex, 1, 'click activated Search');
 * await expectEqual(el.value, 'Search', 'value falls back to label');
 * ```
 *
 * @tutorial exclusive-active
 * ```js
 * // Only one item is active at a time. Activating a new one clears the old.
 * const el = mount(`
 *   <ry-dock>
 *     <ry-dock-item icon="home" label="Home" active></ry-dock-item>
 *     <ry-dock-item icon="search" label="Search"></ry-dock-item>
 *     <ry-dock-item icon="settings" label="Settings"></ry-dock-item>
 *   </ry-dock>
 * `);
 * const items = el.querySelectorAll('[data-ry-target="item"]');
 * el.select(2);
 * await expectEqual(el.activeIndex, 2, 'Settings now active');
 * await expectAttr(items[2], 'aria-current', 'page');
 * await expectEqual(items[0].hasAttribute('active'), false, 'old active cleared');
 * await expectEqual(items[0].hasAttribute('aria-current'), false, 'old aria-current cleared');
 * await expectEqual(el.querySelectorAll('[active]').length, 1, 'exactly one active');
 * ```
 *
 * @tutorial keyboard-arrows-move-focus-only
 * ```js
 * // Wrong assumption: arrows activate (as in ry-tabs). In ry-dock arrows move
 * // focus ONLY; Enter/Space activate. This is the tab-bar manual-activation
 * // pattern.
 * const el = mount(`
 *   <ry-dock>
 *     <ry-dock-item icon="home" label="Home" active></ry-dock-item>
 *     <ry-dock-item icon="search" label="Search"></ry-dock-item>
 *     <ry-dock-item icon="settings" label="Settings"></ry-dock-item>
 *   </ry-dock>
 * `);
 * const items = el.querySelectorAll('[data-ry-target="item"]');
 * items[0].focus();
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight', bubbles: true }));
 * await expectEqual(document.activeElement, items[1], 'ArrowRight moves focus');
 * await expectEqual(el.activeIndex, 0, 'but does NOT activate');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
 * await expectEqual(el.activeIndex, 1, 'Enter activates the focused item');
 * // Wrap: ArrowRight from last → first
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'End', bubbles: true }));
 * await expectEqual(document.activeElement, items[2], 'End focuses last');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight', bubbles: true }));
 * await expectEqual(document.activeElement, items[0], 'ArrowRight from last wraps to first');
 * ```
 *
 * @tutorial aria-current-on-active
 * ```js
 * // The active item carries aria-current="page"; inactive items have none.
 * const el = mount(`
 *   <ry-dock>
 *     <ry-dock-item icon="home" label="Home" active></ry-dock-item>
 *     <ry-dock-item icon="search" label="Search"></ry-dock-item>
 *   </ry-dock>
 * `);
 * const items = el.querySelectorAll('[data-ry-target="item"]');
 * await expectAttr(items[0], 'aria-current', 'page');
 * await expectEqual(items[1].hasAttribute('aria-current'), false);
 * await expectAttr(items[0], 'data-ry-state', 'active');
 * ```
 *
 * @tutorial disabled-item-no-op
 * ```js
 * // Disabled items ignore clicks and select(); no ry:change fires.
 * const el = mount(`
 *   <ry-dock>
 *     <ry-dock-item icon="home" label="Home" active></ry-dock-item>
 *     <ry-dock-item icon="search" label="Search" disabled></ry-dock-item>
 *   </ry-dock>
 * `);
 * const items = el.querySelectorAll('[data-ry-target="item"]');
 * const fired = [];
 * el.addEventListener('ry:change', (e) => fired.push(e.detail.index));
 * items[1].click();
 * await expectEqual(el.activeIndex, 0, 'disabled item not activated by click');
 * el.select(1);
 * await expectEqual(el.activeIndex, 0, 'disabled item not activated by select()');
 * await expectEqual(fired.length, 0, 'no ry:change fired');
 * await expectAttr(items[1], 'aria-disabled', 'true');
 * ```
 *
 * @tutorial keyboard-skips-disabled
 * ```js
 * // Arrow navigation skips disabled items entirely.
 * const el = mount(`
 *   <ry-dock>
 *     <ry-dock-item icon="home" label="Home" active></ry-dock-item>
 *     <ry-dock-item icon="search" label="Search" disabled></ry-dock-item>
 *     <ry-dock-item icon="settings" label="Settings"></ry-dock-item>
 *   </ry-dock>
 * `);
 * const items = el.querySelectorAll('[data-ry-target="item"]');
 * items[0].focus();
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight', bubbles: true }));
 * await expectEqual(document.activeElement, items[2], 'ArrowRight skips disabled middle item');
 * ```
 *
 * @tutorial value-setter-activates
 * ```js
 * const el = mount(`
 *   <ry-dock>
 *     <ry-dock-item icon="home" label="Home" value="home" active></ry-dock-item>
 *     <ry-dock-item icon="search" label="Search" value="search"></ry-dock-item>
 *   </ry-dock>
 * `);
 * const fired = [];
 * el.addEventListener('ry:change', (e) => fired.push(e.detail.value));
 * el.value = 'search';
 * await expectEqual(el.activeIndex, 1, 'value setter activated search');
 * await expectEqual(fired[0], 'search', 'detail.value reflects new value');
 * // Re-setting the same value is a no-op (no extra event)
 * el.value = 'search';
 * await expectEqual(fired.length, 1, 'no event when value unchanged');
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // preventDefault() on ry:beforechange keeps the current item active and
 * // ry:change stays silent — the "unsaved changes before you navigate" guard.
 * const el = mount(`
 *   <ry-dock>
 *     <ry-dock-item icon="home" label="Home" value="home" active></ry-dock-item>
 *     <ry-dock-item icon="search" label="Search" value="search"></ry-dock-item>
 *   </ry-dock>
 * `);
 * const items = el.querySelectorAll('[data-ry-target="item"]');
 * const changed = [];
 * el.addEventListener('ry:change', (e) => changed.push(e.detail.value));
 * el.addEventListener('ry:beforechange', (e) => {
 *   if (e.detail.value === 'search' && e.detail.previous === 'home') e.preventDefault();
 * }, { once: true });
 * items[1].click();
 * await expectEqual(el.activeIndex, 0, 'vetoed click leaves Home active');
 * await expectEqual(el.value, 'home');
 * await expectEqual(changed.length, 0, 'ry:change did not fire');
 * items[1].click();
 * await expectEqual(el.activeIndex, 1, 'without the veto, the click activates Search');
 * await expectEqual(changed[0], 'search', 'ry:change fired with the new value');
 * ```
 *
 * @see ry-tabs
 * @see ry-sidenav
 */

import { RyElement } from '../core/ry-element.js';

interface DockChangeDetail {
  index: number;
  oldIndex: number;
  value: string;
  item: HTMLElement;
}

interface DockBeforeChangeDetail extends DockChangeDetail {
  previous: string;
}

const NAV_KEYS = new Set([
  'ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' ', 'Spacebar',
]);

export class RyDock extends RyElement {
  setup(): void {
    if (!this.hasAttribute('data-ry-target')) {
      this.setAttribute('data-ry-target', 'dock');
    }
    this.setAttribute('role', 'navigation');
    if (!this.hasAttribute('aria-label')) {
      this.setAttribute('aria-label', 'Bottom navigation');
    }

    this.#decorateItems();

    // Ensure exactly one enabled item is active (first enabled if none marked).
    const items = this.#items();
    const activeIndex = items.findIndex(i => i.hasAttribute('active') && !this.#isDisabled(i));
    if (activeIndex === -1) {
      const firstEnabled = items.findIndex(i => !this.#isDisabled(i));
      if (firstEnabled !== -1) this.#applyActive(firstEnabled, items);
    } else {
      this.#applyActive(activeIndex, items);
    }

    this.on(this, 'keydown', this.#handleKeydown);
  }

  #items(): HTMLElement[] {
    return this.$$<HTMLElement>('[data-ry-target="item"]');
  }

  #isDisabled(item: HTMLElement): boolean {
    return item.hasAttribute('disabled') || item.getAttribute('aria-disabled') === 'true';
  }

  #decorateItems(): void {
    const items = this.$$<HTMLElement>(':scope > ry-dock-item');

    items.forEach(item => {
      item.setAttribute('data-ry-target', 'item');
      item.classList.add('ry-dock__item');

      const href = item.getAttribute('href');
      if (href !== null) {
        // Link items are anchors so the browser handles navigation natively.
        if (item.tagName.toLowerCase() === 'ry-dock-item') item.setAttribute('role', 'link');
      } else {
        item.setAttribute('role', 'button');
      }

      // Build icon + label markup if authored via attributes (and not already
      // present as authored children).
      const icon = item.getAttribute('icon');
      const label = item.getAttribute('label');

      if (icon && !item.querySelector('ry-icon, .ry-dock__icon')) {
        const ryIcon = document.createElement('ry-icon');
        ryIcon.setAttribute('name', icon);
        ryIcon.classList.add('ry-dock__icon');
        item.prepend(ryIcon);
      }

      if (label && !item.querySelector('.ry-dock__label, [data-ry-target="label"]')) {
        const span = document.createElement('span');
        span.className = 'ry-dock__label';
        span.setAttribute('data-ry-target', 'label');
        span.textContent = label;
        item.appendChild(span);
      }

      // Accessible name fallback for icon-only items.
      if (!label && icon && !item.hasAttribute('aria-label')) {
        item.setAttribute('aria-label', icon);
      }

      if (this.#isDisabled(item)) {
        item.setAttribute('aria-disabled', 'true');
        item.setAttribute('tabindex', '-1');
      }

      this.on(item, 'click', this.#handleItemClick);
    });
  }

  #handleItemClick = (event: Event): void => {
    const item = event.currentTarget as HTMLElement;
    if (this.#isDisabled(item)) {
      event.preventDefault();
      return;
    }
    const index = this.#items().indexOf(item);
    if (index !== -1) this.select(index);
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    if (!NAV_KEYS.has(event.key)) return;
    const items = this.#items();
    const focused = document.activeElement as HTMLElement;
    const currentIndex = items.indexOf(focused);
    if (currentIndex === -1) return;

    const enabled = items
      .map((item, i) => ({ item, i }))
      .filter(({ item }) => !this.#isDisabled(item));
    if (enabled.length === 0) return;

    const posInEnabled = enabled.findIndex(e => e.i === currentIndex);

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown': {
        event.preventDefault();
        const next = enabled[(posInEnabled + 1) % enabled.length];
        next?.item.focus();
        break;
      }
      case 'ArrowLeft':
      case 'ArrowUp': {
        event.preventDefault();
        const prev = enabled[(posInEnabled - 1 + enabled.length) % enabled.length];
        prev?.item.focus();
        break;
      }
      case 'Home': {
        event.preventDefault();
        enabled[0]?.item.focus();
        break;
      }
      case 'End': {
        event.preventDefault();
        enabled[enabled.length - 1]?.item.focus();
        break;
      }
      case 'Enter':
      case ' ':
      case 'Spacebar': {
        event.preventDefault();
        if (!this.#isDisabled(focused)) this.select(currentIndex);
        break;
      }
    }
  };

  #applyActive(index: number, items: HTMLElement[]): void {
    items.forEach((item, i) => {
      const isActive = i === index;
      if (isActive) {
        item.setAttribute('active', '');
        item.setAttribute('aria-current', 'page');
        item.dataset.ryState = 'active';
        if (!this.#isDisabled(item)) item.setAttribute('tabindex', '0');
      } else {
        item.removeAttribute('active');
        item.removeAttribute('aria-current');
        item.dataset.ryState = '';
        item.setAttribute('tabindex', this.#isDisabled(item) ? '-1' : '0');
      }
    });
  }

  #valueOf(item: HTMLElement): string {
    return item.getAttribute('value') ?? item.getAttribute('label') ?? '';
  }

  /** Activate the item at `index`. No-op if out of range or disabled. */
  select(index: number): void {
    const items = this.#items();
    const item = items[index];
    if (!item || this.#isDisabled(item)) return;

    const oldIndex = this.activeIndex;
    if (oldIndex === index) return; // already active — no event

    const value = this.#valueOf(item);
    if (!this.emit<DockBeforeChangeDetail>('beforechange', {
      index, oldIndex, value, item, previous: this.value,
    })) return;

    this.#applyActive(index, items);
    this.emit<DockChangeDetail>('change', {
      index,
      oldIndex,
      value,
      item,
    });
  }

  get activeIndex(): number {
    return this.#items().findIndex(i => i.dataset.ryState === 'active');
  }

  set activeIndex(index: number) {
    this.select(index);
  }

  get value(): string {
    const items = this.#items();
    const active = items[this.activeIndex];
    return active ? this.#valueOf(active) : '';
  }

  set value(v: string) {
    const items = this.#items();
    const index = items.findIndex(i => this.#valueOf(i) === v);
    if (index !== -1) this.select(index);
  }
}

customElements.define('ry-dock', RyDock);

// ry-dock-item is a decorated-in-place element (like ry-tab). It carries no
// behavior of its own — the parent ry-dock owns selection. Register a bare
// custom element so the parser upgrades it and `:defined` styling applies.
export class RyDockItem extends HTMLElement {}

if (!customElements.get('ry-dock-item')) {
  customElements.define('ry-dock-item', RyDockItem);
}
